import { useEffect, useRef, useState } from "react";
import axios from "axios";
import consts from "./constant";
import { useLocation } from "react-router-dom";

export function useKucoinWebSocketProvider() {
    const [currentSocketToken, setCurrentSocketToken] = useState(null);
    const kucoinSocket = useRef(null);
    const pingInterval = useRef(null);
    const [spotConnected, setSpotConnected] = useState(false);
    const location = useLocation();
    const placeRoute = location.pathname?.split("/")[1] || null;
    const connectionId = useRef(0);

    const routeKey = `${placeRoute}-${currentSocketToken}`;

    // Fetch token once or when route changes
    useEffect(() => {
        const fetchToken = async () => {
            try {
                const url = `${consts.BackendUrl}/kucoin/generatekucointoken`;
                const { data } = await axios.get(url);
                if (data.success) {
                    const cleanToken = data?.result?.replace(/^"(.*)"$/, "$1");
                    setCurrentSocketToken(cleanToken);
                }
            } catch (err) {
                console.error("❌ Error generating KuCoin token:", err);
            }
        };

        if (placeRoute) fetchToken();
    }, [placeRoute]);

    // WebSocket setup effect
    useEffect(() => {
        if (!currentSocketToken || !placeRoute) return;

        connectionId.current += 1;
        const thisConnection = connectionId.current;

        // Cleanup any previous connection
        if (kucoinSocket.current) {
            console.log("🔌 Closing previous socket...");
            kucoinSocket.current.close();
            kucoinSocket.current = null;
        }

        if (pingInterval.current) {
            clearInterval(pingInterval.current);
            pingInterval.current = null;
        }

        setSpotConnected(false);

        const getWsInfo = (route) => {
            if (route === "trade") {
                return {
                    url: `wss://ws-api-spot.kucoin.com?token=${currentSocketToken}`,
                    type: "Spot",
                };
            } else if (route === "future") {
                return {
                    url: `wss://ws-api-futures.kucoin.com?token=${currentSocketToken}`,
                    type: "Futures",
                };
            }
            return null;
        };

        const wsInfo = getWsInfo(placeRoute);
        if (!wsInfo) return;

        console.log("🌐 Connecting to:", wsInfo.url);

        const ws = new WebSocket(wsInfo.url);
        kucoinSocket.current = ws;

        ws.onopen = () => {
            if (connectionId.current === thisConnection) {
                console.log(`✅ ${wsInfo.type} WebSocket connected`);
                setSpotConnected(true);

                // Start ping
                pingInterval.current = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ id: `ping-${Date.now()}`, type: "ping" }));
                    }
                }, 20000);
            }
        };

        ws.onclose = (e) => {
            if (connectionId.current === thisConnection) {
                console.warn(`❌ ${wsInfo.type} WebSocket disconnected`, e.code, e.reason);
                setSpotConnected(false);
            }
        };

        ws.onerror = (err) => {
            if (connectionId.current === thisConnection) {
                console.error(`⚠️ ${wsInfo.type} WebSocket error:`, err);
                setSpotConnected(false);
            }
        };

        return () => {
            if (connectionId.current === thisConnection) {
                if (pingInterval.current) {
                    clearInterval(pingInterval.current);
                    pingInterval.current = null;
                }
                if (kucoinSocket.current) {
                    console.log(`🧹 Cleaning up ${wsInfo.type} WebSocket`);
                    kucoinSocket.current.close();
                    kucoinSocket.current = null;
                }
            }
        };
    }, [routeKey]);

    const sendMessage = (msg) => {
        if (kucoinSocket.current?.readyState === WebSocket.OPEN) {
            kucoinSocket.current.send(JSON.stringify(msg));
        } else {
            console.warn("⚠️ WebSocket is not open. Cannot send message.");
        }
    };

    return {
        kucoinSocket,
        spotConnected,
        sendMessage,
    };
}
